import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { getCompanyBasesList } from "../sharedCode/queries";
import { lookupOptions } from "../GQL/lookupOptions";
import { CompanyBaseExtended, ExtendCompanyBase } from "../Utils/TypeExtensions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { CompanyBase } from "../sharedCode/avroGraphql";

export default function useBaseLocationBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getCompanyBasesList, lookupOptions);

    const fetchData = (firstRun = false) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getCompanyBases.slice() as CompanyBase[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: CompanyBase[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((item: CompanyBase) => ExtendCompanyBase(item))) as CompanyBaseExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.companyBase, async () => {
            await TicketDatabase.companyBase
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.companyBase.bulkDelete(keys);
                })
                .then(() => TicketDatabase.companyBase.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getCompanyBases.slice() as CompanyBase[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.companyBase.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
